'use client';

import { Loader2 } from 'lucide-react';

import { useCallback, useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';

import { toast } from '@/shared/components/toast';
import { KeyboardKeys } from '@/shared/utils/navigation';
import { isValidWorkspaceName, sanitizeWorkspaceName } from '@/shared/utils/strings';

type WorkspaceNameDialogProps = {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	currentName: string;
	onSave: (name: string) => Promise<void>;
	isSaving: boolean;
};

export function WorkspaceNameDialog({
	open,
	onOpenChange,
	currentName,
	onSave,
	isSaving,
}: WorkspaceNameDialogProps) {
	const [nameValue, setNameValue] = useState(currentName);
	const inputRef = useRef<HTMLInputElement>(null);

	// Sync value and focus when dialog opens
	useEffect(() => {
		if (open) {
			setNameValue(currentName);
			inputRef.current?.focus();
		}
	}, [open, currentName]);

	const handleCancel = useCallback(() => {
		setNameValue(currentName);
		onOpenChange(false);
	}, [currentName, onOpenChange]);

	const handleSave = useCallback(async () => {
		const trimmed = nameValue.trim();
		if (!trimmed) {
			toast({ title: 'Name cannot be empty', variant: 'destructive' });
			return;
		}
		if (!isValidWorkspaceName(trimmed)) {
			toast({
				title: 'Invalid workspace name',
				description:
					'Name can only contain letters, numbers, spaces, hyphens, underscores, periods, and ampersands',
				variant: 'destructive',
			});
			return;
		}
		await onSave(trimmed);
		onOpenChange(false);
	}, [nameValue, onSave, onOpenChange]);

	const hasChanged = nameValue.trim() !== currentName.trim();

	return (
		<Dialog
			open={open}
			onOpenChange={onOpenChange}
		>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Change workspace name</DialogTitle>
				</DialogHeader>
				<div className="space-y-4">
					<Input
						ref={inputRef}
						value={nameValue}
						onChange={(e) => setNameValue(sanitizeWorkspaceName(e.target.value))}
						placeholder="Enter workspace name"
						maxLength={100}
						onKeyDown={(e) => {
							if (e.key === KeyboardKeys.Enter && hasChanged) handleSave();
						}}
					/>
					<div className="flex justify-end gap-2">
						<Button
							variant="outline"
							onClick={handleCancel}
							disabled={isSaving}
						>
							Cancel
						</Button>
						<Button
							onClick={handleSave}
							disabled={isSaving || !nameValue?.trim() || !hasChanged}
						>
							{isSaving && <Loader2 className="mr-2 size-4 animate-spin" />}
							Save
						</Button>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
}
